import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Employee, Organization, PreventionAssignment } from '@/types/Api';
import { fetchOrganization, updateEmployeeNote, updatePreventionDeadline } from '@/lib/apiClient';

interface OrganizationState {
  data: Organization | null;
  loading: boolean;
  error: string | null;
  // Simplified tracking of updates for deadlines
  deadlineUpdates: {
    [preventionId: string]: {
      loading: boolean;
      error: string | null;
    };
  };
  // Tracking updates for employee notes
  noteUpdates: {
    [employeeId: string]: {
      loading: boolean;
      error: string | null;
    };
  };
}

const initialState: OrganizationState = {
  data: null,
  loading: false,
  error: null,
  deadlineUpdates: {},
  noteUpdates: {},
};

export const getOrganization = createAsyncThunk<
  Organization,
  { id: string; token: string },
  { rejectValue: string }
>('organizations/getOrganization', async ({ id, token }, thunkAPI) => {
  const { data, error } = await fetchOrganization(id, token);

  if (error) {
    return thunkAPI.rejectWithValue(error.message);
  }
  return data as Organization;
});

export const updatePreventionDeadlineThunk = createAsyncThunk<
  { assignment: PreventionAssignment; employeeId: string },
  { preventionId: string; employeeId: string; newDate: Date; token: string },
  { rejectValue: { message: string; preventionId: string } }
>('organizations/updatePreventionDeadline', async (params, thunkAPI) => {
  const { preventionId, employeeId, newDate, token } = params;
  const { data, error } = await updatePreventionDeadline(preventionId, newDate, token);

  if (error) {
    return thunkAPI.rejectWithValue({
      message: error.message,
      preventionId,
    });
  }

  return {
    assignment: data as PreventionAssignment,
    employeeId,
  };
});

export const updateEmployeeNoteThunk = createAsyncThunk<
  { employeeId: string; employee: Employee },
  { employeeId: string; note: string; token: string },
  { rejectValue: { message: string; employeeId: string } }
>('organizations/updateEmployeeNote', async (params, thunkAPI) => {
  const { employeeId, note, token } = params;
  const { data, error } = await updateEmployeeNote(employeeId, note, token);

  if (error) {
    return thunkAPI.rejectWithValue({
      message: error.message,
      employeeId,
    });
  }

  return {
    employee: data as Employee,
    employeeId,
  };
});

export const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    clearDeadlineUpdateError: (state, action) => {
      const preventionId = action.payload;
      if (state.deadlineUpdates[preventionId]) {
        state.deadlineUpdates[preventionId].error = null;
      }
    },
    clearNoteUpdateError: (state, action) => {
      const employeeId = action.payload;
      if (state.noteUpdates[employeeId]) {
        state.noteUpdates[employeeId].error = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Organization cases
      .addCase(getOrganization.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getOrganization.fulfilled, (state, action) => {
        state.loading = false;
        const mostRecentUpdatedEmployees = action.payload.employees.sort((a, b) =>
          a.updated_at < b.updated_at ? 1 : -1,
        );
        state.data = {
          ...action.payload,
          employees: mostRecentUpdatedEmployees,
        };
      })
      .addCase(getOrganization.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch organization';
      })

      // Update Prevention Deadline cases
      .addCase(updatePreventionDeadlineThunk.pending, (state, action) => {
        const preventionId = action.meta.arg.preventionId;
        state.deadlineUpdates[preventionId] = {
          loading: true,
          error: null,
        };
      })
      .addCase(updatePreventionDeadlineThunk.fulfilled, (state, action) => {
        const { assignment, employeeId } = action.payload;
        const preventionId = assignment.id;

        state.deadlineUpdates[preventionId] = {
          loading: false,
          error: null,
        };

        if (!state.data?.employees) return;

        const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employeeId);
        if (employeeIndex === -1) return;

        const employee = state.data.employees[employeeIndex];
        if (!employee.preventionAssignments) return;

        const assignmentIndex = employee.preventionAssignments.findIndex(
          (a) => a.id === assignment.id,
        );
        if (assignmentIndex === -1) return;

        state.data.employees[employeeIndex].preventionAssignments[assignmentIndex] = assignment;
      })
      .addCase(updatePreventionDeadlineThunk.rejected, (state, action) => {
        const { preventionId, message } = action.payload || {
          preventionId: '',
          message: 'Failed to update due date',
        };

        state.deadlineUpdates[preventionId] = {
          loading: false,
          error: message,
        };
      })

      // Update Employee Note cases
      .addCase(updateEmployeeNoteThunk.pending, (state, action) => {
        const employeeId = action.meta.arg.employeeId;

        // Set loading state in the noteUpdates object
        state.noteUpdates[employeeId] = {
          loading: true,
          error: null,
        };
      })
      .addCase(updateEmployeeNoteThunk.fulfilled, (state, action) => {
        const { employeeId, employee } = action.payload;

        // Update loading state
        state.noteUpdates[employeeId] = {
          loading: false,
          error: null,
        };

        if (!state.data?.employees) return;

        const employeeIndex = state.data.employees.findIndex((emp) => emp.id === employeeId);

        if (employeeIndex !== -1) {
          // Replace the employee with the updated one
          state.data.employees[employeeIndex] = {
            ...state.data.employees[employeeIndex],
            ...employee,
          };
        }
      })
      .addCase(updateEmployeeNoteThunk.rejected, (state, action) => {
        const { employeeId } = action.payload || {
          employeeId: '',
          message: 'common.error',
        };

        // Update error state
        state.noteUpdates[employeeId] = {
          loading: false,
          error: 'common.error',
        };
      });
  },
});

export const { clearDeadlineUpdateError, clearNoteUpdateError } = organizationSlice.actions;
export default organizationSlice.reducer;
